import React, { useEffect, useRef, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { Box } from '@mui/material';
import Grid from '@mui/material/Grid';
import { FormInput } from 'components/molecules/FormInput';
import { FormRadioGroup } from 'components/molecules/FormRadioGroup';
import { EButtonVariants } from 'constants/Buttons';
import Button from 'components/atoms/Button';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormCheckbox } from 'components/molecules/FormCheckbox';
import { initialPersonalHealthStepFormValues } from 'models/forms/applicationForm';
import ApplicationStore from 'state/ApplicationStore';
import {
  StyledStepTitle,
  StyledSectionTitle,
  StyledParagraph,
  StyledList,
} from 'pages/ApplyOnlinePage/ApplyOnlineWizardPage/WizardSteps/styles';
import { usePersonalHealthStepFormSchema } from './schemaValidations';
import AddPreviousGpListItem from './AddPreviousGPListItem';

interface IPersonalHealthStep {
  setNextStep: (arg: number) => void;
}

const PersonalHealthStep = ({ setNextStep }: IPersonalHealthStep) => {
  const [showDiagnoseDetails, setShowDiagnoseDetails] = useState<boolean>(false);
  const [showPeriodsAdditionalInfo, setShowPeriodsAdditionalInfo] = useState<boolean>(false);
  const { schema } = usePersonalHealthStepFormSchema();

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm({
    defaultValues: initialPersonalHealthStepFormValues,
    resolver: yupResolver(schema),
  });

  const { fields, append } = useFieldArray({
    control,
    name: 'previousGPList',
  });

  (window as any).fireCycleStepTwo = () => {
    reset({
      beenDiagnosed: '0',
      doctorName: 'Doctor Name',
      doctorAddress: 'Baker Street 2',
      doctorPostcode: '11-111',
      doctorPhone: '899899899',
      doctorEmail: '',

      previousGPList: [
        {
          doctorName: 'Doc Name 2',
          doctorAddress: 'Baker Street 99',
          doctorPostcode: '22-222',
          doctorPhone: '',
          doctorEmail: '',
        },
      ],
      anyPeriodsGP: '0',
      periodsAdditionalInfo: '',
      downloadApproved: false,
    } as any);
  };

  const formRef = useRef<HTMLFormElement | null>(null);
  const watchDownloadApproved = watch('downloadApproved' as any);
  const watchDiagnoseDetails = watch('beenDiagnosed');
  const watchPeriodsGP = watch('anyPeriodsGP');

  useEffect(() => {
    const handler = () => {
      if (formRef.current) {
        formRef.current.requestSubmit();
      }
    };
    document.addEventListener('submitFormEvent', handler);
    return () => {
      document.removeEventListener('submitFormEvent', handler);
    };
  }, []);

  const addPreviousGP = () => {
    append({
      doctorName: '',
      doctorAddress: '',
      doctorPostcode: '',
      doctorPhone: '',
      doctorEmail: '',
    });
  };

  useEffect(() => {
    const formData = ApplicationStore.getStepData('healthData');
    if (Object.keys(formData).length > 0) {
      reset(formData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setShowDiagnoseDetails(watchDiagnoseDetails === '1');
  }, [watchDiagnoseDetails]);

  useEffect(() => {
    setShowPeriodsAdditionalInfo(watchPeriodsGP === '1');
  }, [watchPeriodsGP]);

  useEffect(() => {
    ApplicationStore.setStepErrors(1, Object.keys(errors).length > 0);
  }, [errors]);

  const onSubmitForm = (data: any) => {
    ApplicationStore.setStepData('healthData', data);
    ApplicationStore.setStepErrors(1, false);
    setNextStep(2);
  };

  return (
    <Box>
      <StyledStepTitle variant="h4">Personal health & medical declaration</StyledStepTitle>
      <StyledParagraph variant="body2">
        Sections 27 and 28 of the Firearms Act 1968 (as amended) specify that in order to issue
        a firearm or shotgun certificate the chief officer of police must be satisfied that an
        applicant can be permitted to possess a gun ‘without danger to the public safety or the
        peace’. Medical fitness is one of the factors police must consider when assessing a
        person’s suitability.
      </StyledParagraph>
      <StyledParagraph variant="body2">
        You must disclose any relevant physical or mental health conditions that you have been
        diagnosed with or treated for in the past as this may affect your ability to safely
        possess and use a firearm or shotgun. Relevant medical conditions which must be
        disclosed are listed below.
      </StyledParagraph>
      <StyledParagraph variant="body2">
        Relevant medical conditions which must be disclosed are:
      </StyledParagraph>
      <StyledList>
        <li>
          Acute Stress Reaction or an acute reaction to the stress caused by a trauma,
          including post-traumatic stress disorder.
        </li>
        <li>Suicidal thoughts or self-harm or harm to others.</li>
        <li>Depression or anxiety.</li>
        <li>Dementia.</li>
        <li>Mania, bipolar disorder or a psychotic illness.</li>
        <li>A personality disorder.</li>
        <li>
          A neurological condition: for example, Multiple Sclerosis, Parkinson’s or
          Huntington’s diseases, or epilepsy.
        </li>
        <li>Alcohol or drug abuse</li>
        <li>
          Any other mental or physical condition, or combination of conditions, which you think
          may be relevant.
        </li>
      </StyledList>
      <StyledParagraph variant="body2">
        If in doubt, consult your doctor or contact the police firearms licensing department.
      </StyledParagraph>
      <StyledParagraph variant="body2">
        With regards to data protection, it should be noted that the medical information will
        be processed on a public interest basis for the legitimate policing purpose of
        assessing the suitability of someone to be granted a firearm or shotgun certificate.
      </StyledParagraph>
      <StyledParagraph variant="body2">
        Medical practitioners have separately requested that an applicant&apos;s consent is
        provided in order for medical practitioners to be satisfied that they have discharged
        their obligations under their duty of confidentiality in relation to their patients.
        The application form requests the applicant&apos;s consent for the release of the
        information for that reason.
      </StyledParagraph>
      <StyledParagraph variant="body2">
        Where the doctor indicates that there are relevant medical issues and police require
        further medical information to consider the application, you should obtain a report
        about these medical issues. You are expected to meet the cost of a fee if it is
        charged. Following this, if police require an additional report to be provided, they
        will meet the cost of the fee charged.
      </StyledParagraph>
      <StyledParagraph variant="body2">
        The police will ask your GP to place an encoded reminder on your patient record to
        indicate that you have been issued with a firearm or shotgun certificate. The GP is
        asked to notify the police if, following issue of the certificate, you are diagnosed
        with or treated for a relevant medical condition (listed in note 5), or if the GP has
        other concerns about your possession of a certificate that might affect your safe
        possession of firearms. Following contact from your GP there may be a need for a
        medical report to be obtained to assist with assessment of your continued suitability
        to possess a firearm or shotgun certificate. The police will pay if a medical report is
        required.
      </StyledParagraph>
      <StyledParagraph variant="body2">
        Following the issue of a firearm or shotgun certificate please note that the
        declaration you have signed consenting to information sharing between your doctor and
        police applies during the application process and during the validity of any firearm or
        shotgun certificate, which may be up to five years.
      </StyledParagraph>
      <StyledParagraph variant="body2">
        You are expected to inform the police if, following issue of the certificate, you are
        diagnosed with or treated for a relevant medical condition while the certificate
        remains valid.
      </StyledParagraph>
      <StyledParagraph variant="body2">
        You should inform the police if you change your GP practice and provide contact details
        for the new practice.
      </StyledParagraph>

      <form onSubmit={handleSubmit(onSubmitForm)} ref={formRef}>
        <Box>
          <StyledSectionTitle variant="body1">
            Have you ever been diagnosed with or treated for any of these medical conditions?
          </StyledSectionTitle>
        </Box>

        <Box>
          <FormRadioGroup
            name="beenDiagnosed"
            label=""
            options={[
              { label: 'YES', value: '1' },
              { label: 'NO', value: '0' },
            ]}
            control={control}
            errors={errors}
          />
          {showDiagnoseDetails && (
            <FormInput
              name="diagnoseDetails"
              label="If YES, provide details"
              control={control}
              errors={errors}
            />
          )}
        </Box>

        <StyledSectionTitle variant="h5">
          Details of your doctor or medical practice
        </StyledSectionTitle>
        <StyledParagraph variant="body2">
          It is your responsibility to arrange for your GP or another suitably qualified
          GMC-registered doctor* (including where a doctor is providing this service for a
          private company) to provide medical information to the police concerning your
          suitability to possess a firearm and/or shotgun.
        </StyledParagraph>
        <StyledParagraph variant="body2">
          Please provide your GP or GP Practice’s details below for the police to share a
          medical proforma for completion. You are expected to meet the cost if a fee is
          charged for this. When the medical information is being provided to the police by a
          doctor from a private company, the doctor must receive the applicant&apos;s medical
          information direct from the GP practice and not via the applicant.
        </StyledParagraph>
        <StyledParagraph variant="body2">
          *A doctor with a full, specialist or GP (rather than provisional) GMC registration
          and a licence to practise.
        </StyledParagraph>

        <Grid container spacing={1}>
          <Grid item xs={12}>
            <FormInput
              name="doctorName"
              label="Name"
              required
              control={control}
              errors={errors}
            />
          </Grid>
        </Grid>

        <Grid container spacing={1}>
          <Grid item xs={8}>
            <FormInput
              name="doctorAddress"
              label="Address"
              required
              control={control}
              errors={errors}
            />
          </Grid>
          <Grid item xs={4}>
            <FormInput
              name="doctorPostcode"
              label="Postcode"
              required
              control={control}
              errors={errors}
            />
          </Grid>
        </Grid>

        <Grid container spacing={1}>
          <Grid item xs={4}>
            <FormInput
              name="doctorPhone"
              label="Telephone Number"
              required
              control={control}
              errors={errors}
            />
          </Grid>
          <Grid item xs={4}>
            <FormInput
              name="doctorEmail"
              label="Email"
              required
              control={control}
              errors={errors}
            />
          </Grid>
        </Grid>

        <StyledSectionTitle variant="h5">
          Details of all previous GP practices during the past 10 years
        </StyledSectionTitle>
        <StyledParagraph variant="body2">
          You are asked to provide details of GP practices over the past 10 years and whether
          you have consulted medical practitioners other than at your GP practice so that all
          relevant information is available to police to assist with their assessment of
          suitability to possess a firearm certificate. Military personnel who are posted
          abroad and have a service GP may still be regarded as resident in the UK for the
          purposes of the application.
        </StyledParagraph>

        <Box>
          {fields.map((field, index) => (
            <AddPreviousGpListItem
              key={field.id}
              arrayName="previousGPList"
              index={index}
              control={control}
              errors={errors}
              addNew={addPreviousGP}
              count={fields.length}
            />
          ))}
        </Box>

        <StyledSectionTitle variant="h5">Other Medical Information</StyledSectionTitle>

        <Box>
          <FormRadioGroup
            name="anyPeriodsGP"
            label="Are there any periods in the past 10 years when you have not been registered with a UK GP or have consulted medical practitioners other than at your GP practice?"
            options={[
              { label: 'YES', value: '1' },
              { label: 'NO', value: '0' },
            ]}
            control={control}
            errors={errors}
          />
          {showPeriodsAdditionalInfo && (
            <FormInput
              name="periodsAdditionalInfo"
              label="Additional information"
              control={control}
              errors={errors}
            />
          )}
        </Box>

        <StyledSectionTitle variant="h5">Duty of confidentiality</StyledSectionTitle>
        <StyledParagraph variant="body2">
          I will arrange for a suitably qualified GMC-registered doctor* to provide factual
          information to the police about any relevant medical conditions related to my
          suitability to possess a firearm or shotgun. I understand that the doctor may share
          my medical records with the police to enable them to make a fully informed decision
          on my application, or on my continued suitability to possess a firearm or shotgun
          while the certificate remains valid, and I consent to this sharing of my medical
          records for confidentiality purposes. I understand that I am expected to inform the
          police if I am diagnosed with, or treated for, a medical condition listed above while
          the certificate remains valid.
        </StyledParagraph>

        <StyledParagraph variant="body2">
          *A doctor with a full, specialist or GP (rather than provisional) GMC registration
          and a licence to practise.
        </StyledParagraph>

        <StyledParagraph variant="body2">
          Note on Data Protection: Personal data (including sensitive personal data) will be
          processed on a public interest basis, for the purpose of allowing the police to
          assess the suitability of an applicant to be granted a firearm or shotgun
          certificate, or to assess the applicant’s continued suitability to possess a firearm
          or shotgun while the certificate remains valid.
        </StyledParagraph>

        <FormCheckbox
          name="downloadApproved"
          label="I understand"
          control={control}
          errors={errors}
        />

        <Button
          variant={EButtonVariants.contained}
          label="Download"
          disabled={!watchDownloadApproved}
        />
      </form>
    </Box>
  );
};

export default PersonalHealthStep;
